import { Fragment, useState } from 'react';
import { Size, TAG_CATEGORY_ICON_MEANINGS, TAG_NOTE_MAX_LENGTH, groupTagsByCategory, isTagCategoryIcon, recordTagPath, t, tagTooltip, type DrawnTag, type PagedRecord } from '@bms2/shared';
import { Button, Textarea } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/modals/Modal';
import { patch } from './api';
import { TagPill } from './TagSwatch';
import pageStyles from './RecordPage.module.css';
import styles from './RecordTags.module.css';

/**
 * 🔒 A record's tags in its banner (decisions 2260, 2261; screens B and C):
 * each pill in its category's color and icon with the tooltip *Category: Tag*
 * and the note beneath, grouped in the Settings order, uncategorized last. A
 * pill's menu edits the record's own note, and its × takes the tag off.
 */
export interface RecordTagControls {
  /** True where the viewer writes the record: the menu and the × draw. */
  writes: boolean;
  onRemove: (tagId: string) => void;
  onEditNote: (tag: DrawnTag) => void;
}

/** One tag in the banner: the pill, its menu where the viewer writes, and its own ×. */
function RecordTagControl({ tag, writes, onRemove, onEditNote }: RecordTagControls & { tag: DrawnTag }) {
  const category = tag.category && tag.category.isActive ? tag.category.name : '';
  return (
    <span className={pageStyles.flagPill} data-record-tag={tag.label} data-record-tag-category={category}>
      {writes ? (
        <details className={styles.menu} data-record-tag-menu={tag.id}>
          <summary className={styles.menuSummary} aria-label={t('record.tagMenu')}>
            <TagPill tag={tag} size={Size.Medium} />
          </summary>
          <div className={styles.menuList}>
            <Button size={Size.Small} text={[tag.note ? t('record.tagNote.edit') : t('record.tagNote.add')]} actions={[() => onEditNote(tag)]} />
          </div>
        </details>
      ) : (
        <TagPill tag={tag} size={Size.Medium} />
      )}
      {writes ? (
        <button type="button" className={pageStyles.clearFlag} aria-label={t('record.removeTag')} onClick={() => onRemove(tag.id)} data-record-remove-tag={tag.id}>
          <Icon meaning="remove" size="inline" />
        </button>
      ) : null}
    </span>
  );
}

/** 🔒 Eight tags or fewer: one row of pills in the banner's action row, already in the grouped order (decision 2260). */
export function RecordTagPills({ tags, ...controls }: RecordTagControls & { tags: readonly DrawnTag[] }) {
  return (
    <>
      {groupTagsByCategory(tags).flatMap((group) => group.tags).map((tag) => <RecordTagControl key={tag.id} tag={tag} {...controls} />)}
    </>
  );
}

/**
 * 🔒 Past eight tags, the grouped strip (decision 2260; screen C): one line per
 * category, its icon and name at the left and its pills beside them, with
 * Other last for the uncategorized.
 */
export function RecordTagStrip({ tags, ...controls }: RecordTagControls & { tags: readonly DrawnTag[] }) {
  return (
    <div className={styles.strip} data-record-tag-strip="true">
      {groupTagsByCategory(tags).map((group) => {
        const name = group.category?.name ?? t('tag.other');
        const icon = group.category && isTagCategoryIcon(group.category.icon) ? group.category.icon : 'tag';
        return (
          <Fragment key={group.category?.id ?? 'other'}>
            <span className={styles.groupName} data-record-tag-group={name}>
              <Icon meaning={TAG_CATEGORY_ICON_MEANINGS[icon]} size="inline" />
              {name}
            </span>
            <span className={styles.groupPills}>
              {group.tags.map((tag) => <RecordTagControl key={tag.id} tag={tag} {...controls} />)}
            </span>
          </Fragment>
        );
      })}
    </div>
  );
}

/** 🔒 Each note on this record's tags, in full, beneath the banner's tags (decision 2261). */
export function RecordTagNotes({ tags }: { tags: readonly DrawnTag[] }) {
  const noted = groupTagsByCategory(tags).flatMap((group) => group.tags).filter((tag) => tag.note);
  if (noted.length === 0) return null;
  return (
    <ul className={styles.notes} data-record-tag-notes="true">
      {noted.map((tag) => (
        <li key={tag.id} className={styles.note} data-record-tag-note={tag.id}>
          <span className={styles.noteHead}>{tagTooltip({ ...tag, note: null }, t('tag.noCategory'))}</span>
          <span className={styles.noteText}>{tag.note}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * 🔒 The pill menu's note window (decision 2261): the record's own note on its
 * tag, never the shared label's. 🔨 Keyed by its caller to the tag it edits,
 * so reopening it on another pill never shows the first pill's draft.
 */
export function TagNoteModal({ tag, recordType, recordId, apiBaseUrl = '', onCancel, onSaved }: {
  tag: DrawnTag;
  recordType: PagedRecord;
  recordId: string;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [note, setNote] = useState(tag.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setError(null);
    const answer = await patch(recordTagPath(recordType, recordId, tag.id), { note }, apiBaseUrl);
    if (!answer.ok) return setError(answer.message);
    onSaved();
  };
  return (
    <Modal
      open
      title={t('record.tagNote.title')}
      subtitle={tagTooltip({ ...tag, note: null }, t('tag.noCategory'))}
      onCancel={onCancel}
      dataModal="tag-note"
      actions={[
        { kind: 'primary', label: t('record.tagNote.save'), onAct: () => void save(), disabled: note.trim().length > TAG_NOTE_MAX_LENGTH },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      <div data-tag-note-editor={tag.id}>
        {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
        <Textarea size={Size.Medium} label={t('record.tagNote.label')} value={note} onChange={setNote} />
        <p className={pageStyles.hint}>{t('record.tagNote.hint')}</p>
      </div>
    </Modal>
  );
}
